import { useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { ElementRenderer } from "./element-renderer"
import { AlertDialog } from "../components/ui/alert-dialog"

/**
 * AlertDialogRenderer — wraps children with shadcn AlertDialog Root.
 *
 * Children should include AlertDialogTrigger (wrapping a button) and
 * AlertDialogContent. Open state can be:
 *  - Uncontrolled: Radix manages it via trigger click (no `bind` / `open`)
 *  - Bound: element.bind = "#{ui.dialogOpen}" → state-controlled via ActionFlow
 *  - defaultOpen: element.defaultOpen = true
 *
 * When bound, onOpenChange fires a synthetic "openChange" event that
 * ActionFlow can wire up to setState to close the dialog.
 */
export function AlertDialogRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()

  const [bound, setBound] = useBoundValue<boolean>(
    element.bind,
    resolveSymbol,
    (element as any).defaultOpen === true,
    Boolean,
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (element.bind !== undefined) setBound(open)
      dispatchEvent(element.id, "openChange", { open })
    },
    [element.id, element.bind, setBound, dispatchEvent],
  )

  const rootProps: Record<string, unknown> =
    element.bind !== undefined
      ? { open: bound, onOpenChange: handleOpenChange }
      : (element as any).defaultOpen !== undefined
        ? { defaultOpen: (element as any).defaultOpen === true, onOpenChange: handleOpenChange }
        : { onOpenChange: handleOpenChange }

  return (
    <div
      className={element.className}
      data-testid={`alert-dialog-${element.id}`}
      data-atom-type="alert-dialog"
      data-open={element.bind !== undefined ? String(bound) : undefined}
    >
      <AlertDialog {...rootProps}>
        {element.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </AlertDialog>
    </div>
  )
}
